"use client";

import { useEffect, useState, type ReactNode } from "react";
import { usePdpVariantNavigation } from "./variant-navigation-context";
import { useFormStatus } from "react-dom";
import { ShoppingBag } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import styles from "./pdp-layout.module.css";

interface StickyBarProps {
	productName: string;
	priceContent: ReactNode;
	expressBadge?: ReactNode;
	show: boolean;
}

export function StickyBar({ productName, priceContent, expressBadge, show }: StickyBarProps) {
	const [visible, setVisible] = useState(false);
	const t = useTranslations("pdpUx.buyBox");
	const tProduct = useTranslations("product");
	const { pending } = useFormStatus();
	const selectionPending = usePdpVariantNavigation()?.pending ?? false;

	useEffect(() => {
		if (!show || !("IntersectionObserver" in window)) return;
		const primaryButton = document.querySelector("#pdp-atc-form [data-pdp-primary-atc]");
		if (!primaryButton) return;

		const observer = new IntersectionObserver(([entry]) => {
			if (entry) setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
		});
		observer.observe(primaryButton);
		return () => observer.disconnect();
	}, [show, priceContent]);

	if (!show || !visible) return null;

	return (
		<div
			className={cn(
				styles.stickyBar,
				"fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card px-4 pt-3 shadow-[0_-8px_24px_rgba(8,39,74,0.10)] min-[900px]:hidden",
				"pb-[max(0.75rem,env(safe-area-inset-bottom))]",
			)}
		>
			<div className="mb-2.5 flex items-center justify-between gap-3">
				<div className="min-w-0 flex-1">
					<p className="truncate text-[0.72rem] text-muted-foreground">{productName}</p>
					<div className="font-display text-[1.1rem] font-medium tracking-[-0.012em] text-primary">
						{priceContent}
					</div>
				</div>
				{expressBadge}
			</div>
			<button
				type="submit"
				form="pdp-atc-form"
				disabled={pending || selectionPending}
				aria-busy={pending || selectionPending || undefined}
				className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary-hero px-4 font-display text-[0.95rem] font-medium tracking-[-0.005em] text-primary-hero-foreground shadow-md transition-colors duration-200 hover:bg-primary"
			>
				<ShoppingBag className="h-4 w-4" strokeWidth={2} aria-hidden />
				{selectionPending ? tProduct("variantUpdating") : pending ? tProduct("adding") : t("addToCart")}
			</button>
		</div>
	);
}
